import {formatMedicationDose} from '../../shared/medicationDose';
import { buildMedicationFromSuggestion } from './medicationSourceSuggestions';
import {
  readCanonicalMedicationRecord,
  requireCanonicalMedicationAction,
} from '../medications/canonicalMedicationRecord';

const normalizeWhitespace = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();

export const normalizeMedicationName = (value = '') => (
  normalizeWhitespace(value)
    .toLowerCase()
    .replace(/\([^)]*\)/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
);

const uniqueValues = (values = []) => [...new Set(values.map(normalizeWhitespace).filter(Boolean))];

const mergeNotes = (existingNote = '', nextNote = '') => (
  uniqueValues([existingNote, nextNote]).join('\n')
);

export const getMedicationNameAliases = (entry = {}) => {
  const aliases = [];
  const addAlias = (value) => {
    const clean = normalizeWhitespace(value);
    if (clean) aliases.push(clean);
  };

  addAlias(entry.name);
  addAlias(entry.genericName);
  addAlias(entry.brandName);
  if (Array.isArray(entry.aliases)) {
    entry.aliases.forEach(addAlias);
  }

  const parentheticalMatches = normalizeWhitespace(entry.name).matchAll(/\(([^)]+)\)/g);
  [...parentheticalMatches].forEach((match) => addAlias(match[1]));
  const withoutParenthetical = normalizeWhitespace(entry.name).replace(/\([^)]*\)/g, '').trim();
  addAlias(withoutParenthetical);

  return uniqueValues(aliases);
};

const getMedicationNameKeys = (entry = {}) => (
  getMedicationNameAliases(entry)
    .map(normalizeMedicationName)
    .filter((value) => value.length > 1)
);

export const findMatchingMedication = (suggestion = {}, existingMedications = []) => {
  const suggestionKeys = new Set(getMedicationNameKeys(suggestion));
  if (!suggestionKeys.size) return null;

  return (Array.isArray(existingMedications) ? existingMedications : []).find((medication) => {
    if (medication?.isArchived) return false;
    return getMedicationNameKeys(medication).some((key) => suggestionKeys.has(key));
  }) || null;
};

const getPrimarySchedule = (medication = {}) => {
  const schedules = Array.isArray(medication.schedules) ? medication.schedules : [];
  return schedules[0] || {};
};

const getDoseSignature = (medication = {}) => {
  const schedule = getPrimarySchedule(medication);
  const dose = normalizeWhitespace(schedule.dose || medication.dose || '');
  const unit = normalizeWhitespace(schedule.unit || medication.unit || '');
  return formatMedicationDose({dose,unit});
};

const getScheduleSignature = (medication = {}) => {
  const schedule = getPrimarySchedule(medication);
  return normalizeWhitespace(
    medication.sourceScheduleText
    || medication.schedule
    || medication.scheduleText
    || schedule.label
    || schedule.time
    || medication.time
    || ''
  ).toLowerCase();
};

const getStatusSignature = (medication = {}) => {
  const status = normalizeWhitespace(medication.status || medication.sourceStatus || '');
  if (status) return status.toLowerCase();
  return medication.isArchived ? 'paused' : 'active';
};

export const getMedicationPlanChanges = (suggestion = {}, matchedMedication = null) => {
  if (!matchedMedication) {
    return {
      doseChanged: false,
      scheduleChanged: false,
      statusChanged: false,
      hasChanges: true,
    };
  }

  const extractedDose = formatMedicationDose(suggestion);
  const existingDose = getDoseSignature(matchedMedication);
  const extractedSchedule = normalizeWhitespace(suggestion.schedule || suggestion.sourceScheduleText || '').toLowerCase();
  const existingSchedule = getScheduleSignature(matchedMedication);
  const extractedStatus = normalizeWhitespace(suggestion.status || '').toLowerCase();
  const existingStatus = getStatusSignature(matchedMedication);
  const doseChanged = Boolean(extractedDose && extractedDose !== existingDose);
  const scheduleChanged = Boolean(extractedSchedule && extractedSchedule !== existingSchedule);
  const statusChanged = Boolean(extractedStatus && extractedStatus !== existingStatus);

  return {
    doseChanged,
    scheduleChanged,
    statusChanged,
    hasChanges: doseChanged || scheduleChanged || statusChanged,
  };
};

const getSourceTitle = (source = {}) => (
  source.sourceMetadata?.fileName
  || source.metadata?.source?.fileName
  || source.fileName
  || 'Medication source'
);

const getSourceRef = (source = {}) => ({
  sourceId: source.id || '',
  title: getSourceTitle(source),
  addedAt: new Date().toISOString(),
});

const decoratePlanHistory = (history = [], suggestion = {}, source = {}) => {
  const sourceRef = getSourceRef(source);
  return (Array.isArray(history) ? history : []).map((entry, index) => ({
    id: entry.id || `${suggestion.id || 'medication'}-history-${index}`,
    ...entry,
    sourceId: sourceRef.sourceId,
    sourceTitle: sourceRef.title,
    sourceDocument: sourceRef,
  }));
};

const buildFallbackChangeHistory = ({
  suggestion = {},
  matchedMedication = null,
  source = {},
  changes = {},
} = {}) => {
  if (!matchedMedication || !changes.hasChanges) return [];

  const existingDose = getDoseSignature(matchedMedication);
  const extractedDose = formatMedicationDose(suggestion);
  const sourceRef = getSourceRef(source);

  return [{
    id: `${suggestion.id || 'medication'}-history-change`,
    type: 'plan_change',
    previousDose: matchedMedication.dose || getPrimarySchedule(matchedMedication).dose || '',
    previousUnit: matchedMedication.unit || getPrimarySchedule(matchedMedication).unit || '',
    newDose: suggestion.dose || '',
    newUnit: suggestion.unit || '',
    schedule: suggestion.schedule || '',
    reason: suggestion.changeReason || '',
    approximateDateText: suggestion.changeDateText || '',
    dateApproximate: Boolean(suggestion.changeDateText),
    summary: [existingDose, extractedDose].filter(Boolean).join(' -> '),
    sourceId: sourceRef.sourceId,
    sourceTitle: sourceRef.title,
    sourceDocument: sourceRef,
    needsReview: Boolean(!suggestion.changeDateText || suggestion.changeDateApproximate || !suggestion.changeReason),
    reviewReasons: [
      ...((!suggestion.changeDateText || suggestion.changeDateApproximate) ? ['exact_date_needed'] : []),
      ...(!suggestion.changeReason ? ['reason_needed'] : []),
    ],
  }];
};

const mergeById = (existing = [], additions = []) => {
  const seen = new Set();
  return [...(Array.isArray(existing) ? existing : []), ...(Array.isArray(additions) ? additions : [])]
    .filter((entry) => {
      const key = entry?.id || JSON.stringify(entry);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

export const buildMedicationPlanCandidate = ({
  suggestion = {},
  source = {},
  existingMedications = [],
} = {}) => {
  const canonicalMedicationRecord = readCanonicalMedicationRecord(suggestion);
  const canonicalAction = requireCanonicalMedicationAction(canonicalMedicationRecord);
  const actionDetails = canonicalMedicationRecord.actionDetails || {};
  const completedCourse = suggestion.completedCourse
    || (canonicalAction === 'complete_course' ? actionDetails : null);
  const temporaryHold = suggestion.temporaryHold
    || (canonicalAction === 'temporary_hold' ? actionDetails : null);
  const historicalSnapshot = suggestion.historicalSnapshot
    || (canonicalAction === 'historical_snapshot' ? actionDetails : null);
  const matchedMedication = findMatchingMedication(suggestion, existingMedications);
  const isTemporaryHold = canonicalAction === 'temporary_hold' && Boolean(temporaryHold);
  const isCourseCompletion = canonicalAction === 'complete_course';
  const isHistoricalSnapshot = canonicalAction === 'historical_snapshot' && Boolean(historicalSnapshot);
  const needsClarification = canonicalAction === 'needs_clarification';
  const changes = getMedicationPlanChanges(suggestion, matchedMedication);
  const suggestedMedication = buildMedicationFromSuggestion(suggestion, source);
  const extractedHistory = decoratePlanHistory(suggestion.history, suggestion, source);
  const fallbackHistory = buildFallbackChangeHistory({
    suggestion,
    matchedMedication,
    source,
    changes,
  });
  const planHistoryEntries = extractedHistory.length ? extractedHistory : fallbackHistory;
  const temporaryHoldHistory = isTemporaryHold && matchedMedication ? [{
    id: `${suggestion.id || 'medication'}-temporary-hold`,
    type: 'temporary_hold',
    ...temporaryHold,
    sourceText: suggestion.sourceText || '',
    sourceId: source.id || '',
    sourceTitle: getSourceTitle(source),
    sourceDocument: getSourceRef(source),
    needsReview: false,
  }] : [];
  const matchedSourceDocuments = Array.isArray(matchedMedication?.sourceDocuments)
    ? matchedMedication.sourceDocuments
    : [];
  const sourceDocuments = mergeById(matchedSourceDocuments, suggestedMedication.sourceDocuments);
  const planHistory = mergeById(matchedMedication?.planHistory, [
    ...planHistoryEntries,
    ...temporaryHoldHistory,
  ]);
  const isUpdate = Boolean(matchedMedication && (changes.hasChanges || isTemporaryHold));
  const isNoChange = Boolean(matchedMedication && !changes.hasChanges);
  const suggestedAction = isHistoricalSnapshot
    ? 'historical_snapshot'
    : isCourseCompletion
    ? 'complete_course'
    : isTemporaryHold
    ? (matchedMedication ? 'temporary_hold' : 'match_required')
    : needsClarification
    ? 'needs_clarification'
    : matchedMedication
    ? (isUpdate ? 'update_existing' : 'no_change')
    : 'add_new';
  const nextMedication = needsClarification
    ? (matchedMedication || suggestedMedication)
    : matchedMedication
    ? {
        ...matchedMedication,
        ...suggestedMedication,
        id: matchedMedication.id,
        name: matchedMedication.name || suggestedMedication.name,
        notes: mergeNotes(matchedMedication.notes, suggestedMedication.notes),
        sourceDocuments,
        planHistory,
        history: Array.isArray(matchedMedication.history) ? matchedMedication.history : [],
        temporaryHold: isTemporaryHold ? {
          ...temporaryHold,
          sourceId: source.id || '',
          sourceTitle: getSourceTitle(source),
        } : matchedMedication.temporaryHold || null,
      }
    : {
        ...suggestedMedication,
        sourceDocuments,
        planHistory,
      };

  return {
    id: suggestion.id,
    suggestion,
    matchedMedication,
    changes,
    suggestedAction,
    actionLabel: isHistoricalSnapshot
      ? 'Review medication history'
      : isCourseCompletion
      ? (matchedMedication ? 'Complete medication course' : 'Save completion to Timeline')
      : isTemporaryHold
      ? (matchedMedication ? 'Apply temporary hold' : 'Match medication first')
      : isUpdate ? 'Update medication' : isNoChange ? 'No plan change found' : 'Add new medication',
    nextMedication,
    planHistoryEntries,
  };
};

export const buildMedicationPlanCandidates = ({
  suggestions = [],
  source = {},
  existingMedications = [],
} = {}) => (
  (Array.isArray(suggestions) ? suggestions : []).map((suggestion) => buildMedicationPlanCandidate({
    suggestion,
    source,
    existingMedications,
  }))
);
